import React, { useState, useEffect, useRef, useCallback, Suspense } from "react";
import lazy from "../utilities/lazyWithRetry";
import { withSentryReactRouterV6Routing } from "@sentry/react";
import { ProvideAuth } from "../services/use-auth";
import { BrowserRouter, Navigate, Route, Routes, useLocation } from "react-router-dom";
import { ThemeProvider } from "../theme/ThemeContext";
import LoadingGuard from "../services/loading_guard";
import ActivityTracker from "../services/activity_tracker";
import { CircularProgress, Box, useMediaQuery } from "@mui/material";

import {
  MainContainer,
  ContentContainer,
  TopNavAndContentContainer,
} from "./app_styles.js";

import Nav from "../nav/nav.js";
import UserProvider from "../services/context_and_index";
import OrgUrlManager from "../services/org_url_manager";
import TopNav from "../nav/top_nav_themed.jsx";
import MaintenanceGuard from "../services/maintenance_guard";
import ControlBanner from "../components/ControlBanner";
import BrokenOrgModal from "../nav/BrokenOrgModal.jsx";
import ChatwootWidget from "../components/ChatwootWidget";
import TourProvider from "../tour/TourProvider";
import { paletteEnabled, ledgerEnabled, trialEnabled } from "../catalog/featureGate";
import TrialBanner from "../components/trial/TrialBanner";
import EmailPreviewModal from "../components/trial/EmailPreviewModal";
import { useLedgerOpen } from "../ledger/ledgerStore";
import ChunkErrorBoundary from "../components/ChunkErrorBoundary";

// Eagerly load auth pages (needed immediately)
import SignInPage from "../session_management/signIn";
import SignUpPage from "../session_management/signUp";
import PasswordResetPage from "../session_management/forgot_password";
import SetNewPasswordPage from "../session_management/SetNewPassword";
import UnonboardedOrgGuard from "../locations/onboarding/llm/UnonboardedOrgGuard";

const SentryRoutes = withSentryReactRouterV6Routing(Routes);

// Command palette (guided navigation) — mounted lazily behind paletteEnabled().
const CommandPalette = lazy(() => import("../catalog/CommandPalette.jsx"));

// Ledger assistant drawer (wave 2) — mounted lazily behind ledgerEnabled().
const LedgerDrawer = lazy(() => import("../ledger/LedgerDrawer.jsx"));

// Docked-drawer width (03 §3.3); the app content is squeezed by this much at ≥1280.
const LEDGER_DRAWER_W = 380;
// Persisted nav-collapse preference (the W1-deferred localStorage item, 03 §3.3).
const LS_NAV_COLLAPSED = "t3.navCollapsed";

// Shared reports pages (unprotected, for external viewers)
// Migration instrument panel (N2). Not on the nav; delete when the domains it
// previews have actually ported.
const PostgresProofPage = lazy(() => import("../locations/migration/PostgresProofPage.jsx"));
const SharedReportEmailEntry = lazy(() => import("../locations/shared-reports/SharedReportEmailEntry"));
const SharedReportViewer = lazy(() => import("../locations/shared-reports/SharedReportViewer"));

// TTC external pages (unprotected, for Decision Makers via magic link)
const TTCEmailEntry = lazy(() => import("../locations/ttc/external/TTCEmailEntry"));
const TTCReviewPage = lazy(() => import("../locations/ttc/external/TTCReviewPage"));

// Lazy load all route components
const Account = lazy(() => import("../parent_nav_components/account.js"));
const Foundation = lazy(() => import("../parent_nav_components/foundation.js"));
const ContactsPage = lazy(() => import("../locations/contacts/contacts_page.jsx"));
const DonorsPage = lazy(() => import("../locations/contacts/donors_page.jsx"));
const History = lazy(() => import("../locations/history/history"));
const OnboardingChat = lazy(() => import("../locations/onboarding/llm/OnboardingChat"));

const AdminT3 = lazy(() => import("../locations/admin/admin_t3.jsx"));
const AdminOrgs = lazy(() => import("../locations/admin/admin_orgs.jsx"));
const AdminManagement = lazy(() => import("../locations/admin/admin_management.jsx"));
const UnitTests = lazy(() => import("../locations/admin/UnitTests.jsx"));
const OrgSetupReport = lazy(() => import("../locations/admin/OrgSetupReport.jsx"));
const AdminMail = lazy(() => import("../locations/admin/mail/index.jsx"));
const SequenceEditorPage = lazy(() => import("../locations/admin/mail/SequenceEditorPage.jsx"));

// TTC (Transactions to Change) pages
const AdminBookkeeping = lazy(() => import("../locations/admin/admin_bookkeeping.jsx"));
const FirmManagementDashboard = lazy(() => import("../locations/firm-management/FirmManagementDashboard.jsx"));
const BookkeeperDashboard = lazy(() => import("../locations/bookkeeper/BookkeeperDashboard.jsx"));
const TTCDashboard = lazy(() => import("../locations/ttc/internal/TTCDashboard.jsx"));
const TTCBatchDetail = lazy(() => import("../locations/ttc/internal/TTCBatchDetail.jsx"));
const DecisionMakerManagement = lazy(() => import("../locations/ttc/internal/DecisionMakerManagement.jsx"));

// Public pages (no auth required)
const ExtendTrialPage = lazy(() => import("../locations/public/ExtendTrialPage.jsx"));
const UnsubscribePage = lazy(() => import("../locations/public/UnsubscribePage.jsx"));
const AuthMagicPage = lazy(() => import("../locations/public/AuthMagicPage.jsx"));
const InvoicePayPage = lazy(() => import("../locations/public/InvoicePayPage.jsx"));
const ChartOfAccounts = lazy(() => import("../locations/foundation/chart_of_accounts/chart_of_accounts.jsx"));
const FundsList = lazy(() => import("../locations/foundation/funds_list/funds_list.jsx"));
const LabelsList = lazy(() => import("../locations/foundation/labels_list/labels_list.jsx"));
const Budgets = lazy(() => import("../locations/foundation/budgets/budgets.js"));
const ConsolidationMapping = lazy(() => import("../locations/foundation/consolidation/consolidation_mapping.jsx"));
const BankAccounts = lazy(() => import("../locations/banking/banking/bank_accounts.js"));
const CreditCards = lazy(() => import("../locations/banking/credit_cards/credit_cards.js"));
const HandleImportedTransactions = lazy(() => import("../locations/banking/handle_imported_transactions.jsx"));
const Reconciliation = lazy(() => import("../locations/banking/reconciliation/reconciliation.js"));
const AutoReconciliationStatus = lazy(() => import("../locations/banking/reconciliation/auto_reconciliation_status.jsx"));
const AutoReconciliationReview = lazy(() => import("../locations/banking/reconciliation/auto_reconciliation_review.jsx"));
const JournalEntry = lazy(() => import("../locations/transactions/journal_entry.js"));
const PayablesReceivables = lazy(() => import("../locations/apar/PayablesReceivables.jsx"));
const BalanceSheet = lazy(() => import("../locations/reports/balance_sheet/balance_sheet.js"));
const IncomeStatement = lazy(() => import("../locations/reports/income_statement/IncomeStatement.jsx"));
const GeneralLedger = lazy(() => import("../locations/reports/general_ledger.js"));
const FundSummary = lazy(() => import("../locations/reports/fund_summary.js"));
const DonationStatements = lazy(() => import("../locations/reports/donation_statements.jsx"));
const ReportsLibraryPage = lazy(() => import("../locations/reports/library/ReportsLibraryPage.jsx"));
const ReportsIndexRedirect = lazy(() => import("../locations/reports/ReportsIndexRedirect.jsx"));
const CustomReportsPage = lazy(() => import("../locations/reports/custom-reports/CustomReportsPage.jsx"));
const OrgInfo = lazy(() => import("../locations/account/org_management/org_info.js"));
const Users = lazy(() => import("../locations/account/users.js"));
const UserManagement = lazy(() => import("../locations/account/settings/user_management.jsx"));
const SubscriptionManagement = lazy(() => import("../locations/account/settings/SubscriptionManagement.jsx"));
const BillingPage = lazy(() => import("../locations/account/billing/BillingPage.jsx"));
const Preferences = lazy(() => import("../locations/account/preferences.js"));
const Home = lazy(() => import("../locations/home/home.js"));
const ReconciliationReport = lazy(() => import("../locations/reports/reconciliation.jsx"));
const Donations = lazy(() => import("../locations/donations/donations.jsx"));
const SourceManagementPage = lazy(() => import("../locations/donations/donation_sources/SourceManagementPage.jsx"));
const TransactionsList = lazy(() => import("../locations/reports/transactions_list.jsx"));

// Foundation Donations pages (page-based architecture for OAuth flows)
const DonationsHub = lazy(() => import("../locations/foundation/donations/index.jsx"));
const DonationSourcesListPage = lazy(() => import("../locations/foundation/donations/SourcesListPage.jsx"));
const SelectSourceType = lazy(() => import("../locations/foundation/donations/connect/SelectSourceType.jsx"));
const ConnectPushpay = lazy(() => import("../locations/foundation/donations/connect/ConnectPushpay.jsx"));
const ConnectT3Donations = lazy(() => import("../locations/foundation/donations/connect/ConnectT3Donations.jsx"));
const PushpayFundMapping = lazy(() => import("../locations/foundation/donations/connect/PushpayFundMapping.jsx"));
const ConnectManualSource = lazy(() => import("../locations/foundation/donations/connect/ConnectManualSource.jsx"));
const IntegrationsHub = lazy(() => import("../locations/foundation/integrations/index.jsx"));
const BillComMappingPage = lazy(() => import("../locations/foundation/integrations/billcom/BillComMappingPage.jsx"));
const PayrollHub = lazy(() => import("../locations/payroll/PayrollHub.jsx"));
const WelcomeToTheClub = lazy(() => import("../locations/welcome_to_the_club"));
const ThemeDemo = lazy(() => import("../theme/ThemeDemo"));
const NoMatch = lazy(() => import("../nav/no_match"));
const MaintenancePage = lazy(() => import("../locations/maintenance/MaintenancePage"));

// Loading fallback component
const LoadingFallback = () => (
  <Box
    display="flex"
    justifyContent="center"
    alignItems="center"
    minHeight="400px">
    <CircularProgress />
  </Box>
);

const App = () => {
  const AppInSession = () => {
    const location = useLocation();
    const isWide = useMediaQuery("(min-width:1280px)");
    const { open: ledgerOpen } = useLedgerOpen();

    // Nav collapse: seed from the user's stored preference (the W1-deferred
    // persistence item lands here, 03 §3.3). User toggles persist; drawer-driven
    // collapse must NOT overwrite the stored preference.
    const [collapsed, setCollapsed] = useState(() => {
      try {
        return localStorage.getItem(LS_NAV_COLLAPSED) === "true";
      } catch {
        return false;
      }
    });
    const userPrefRef = useRef(collapsed);
    const ledgerDrivenRef = useRef(false);

    // Wrapped setter handed to the nav: a user-initiated toggle persists and
    // updates the remembered preference.
    const setCollapsedByUser = useCallback((next) => {
      setCollapsed((prev) => {
        const val = typeof next === "function" ? next(prev) : next;
        userPrefRef.current = val;
        try {
          localStorage.setItem(LS_NAV_COLLAPSED, String(val));
        } catch {
          /* localStorage may be unavailable — non-fatal */
        }
        return val;
      });
    }, []);

    // Opening the Ledger drawer collapses the nav (remembering the user's pref);
    // closing restores it — the stored preference is left untouched throughout.
    useEffect(() => {
      if (!ledgerEnabled()) return;
      if (ledgerOpen && !ledgerDrivenRef.current) {
        ledgerDrivenRef.current = true;
        setCollapsed(true);
      } else if (!ledgerOpen && ledgerDrivenRef.current) {
        ledgerDrivenRef.current = false;
        setCollapsed(userPrefRef.current);
      }
    }, [ledgerOpen]);

    const dockedSqueeze = ledgerEnabled() && ledgerOpen && isWide;

    // Check if current path should hide TopNav
    const shouldHideTopNav =
      location.pathname === "/" ||
      location.pathname === "/history" ||
      /^\/banking\/[^/]+\/handle-imported(\/|$)/.test(location.pathname);

    // Check if we're on auth pages that don't need protection
    const isAuthPage = ["/signin", "/signup", "/reset-password"].includes(
      location.pathname,
    );

    // Set-new-password page: logged-in users forced to change a temporary
    // password. Rendered full-screen (auth design) outside the app chrome.
    const isSetPasswordPage = location.pathname === "/set-new-password";

    // Check if we're on shared reports pages (unprotected for external viewers)
    const isSharedReportsPage = location.pathname.startsWith("/shared-reports/");

    // Check if we're on TTC external pages (unprotected for Decision Makers)
    const isTtcExternalPage = location.pathname.startsWith("/ttc/");

    // Check if we're on public pages (no auth required)
    const isPublicPage = location.pathname.startsWith("/extend-trial") ||
      location.pathname.startsWith("/unsubscribe") ||
      location.pathname.startsWith("/auth/magic") ||
      location.pathname.startsWith("/pay/");

    // Pass db and org to your UserProvider
    return (
      <UserProvider>
        <ActivityTracker>
          <OrgUrlManager />
          <BrokenOrgModal />
          {isAuthPage ? (
            // Auth pages don't need LoadingGuard protection.
            // ChatwootWidget renders unauthenticated for the "I forgot my
            // email/password" support-bot flow; identity sync only fires
            // once a user is authenticated (handled inside the widget).
            <>
              <ChatwootWidget />
              <SentryRoutes>
                <Route path="/signin" element={<SignInPage />} />
                <Route path="/signup" element={<SignUpPage />} />
                <Route path="/reset-password" element={<PasswordResetPage />} />
              </SentryRoutes>
            </>
          ) : isSetPasswordPage ? (
            // Forced password change - requires a logged-in user, no org needed.
            // Full-screen auth design; LoadingGuard waits for auth to resolve
            // and redirects to sign-in if there's no user.
            <LoadingGuard requireUser={true} requireOrg={false}>
              <SentryRoutes>
                <Route
                  path="/set-new-password"
                  element={<SetNewPasswordPage />}
                />
              </SentryRoutes>
            </LoadingGuard>
          ) : isSharedReportsPage ? (
            // Shared reports pages - unprotected for external viewers
            <ChunkErrorBoundary>
              <Suspense fallback={<LoadingFallback />}>
                <SentryRoutes>
                  <Route path="/shared-reports/:shareId" element={<SharedReportEmailEntry />} />
                  <Route path="/shared-reports/:shareId/view" element={<SharedReportViewer />} />
                </SentryRoutes>
              </Suspense>
            </ChunkErrorBoundary>
          ) : isTtcExternalPage ? (
            // TTC external pages - unprotected for Decision Makers (magic link auth)
            <ChunkErrorBoundary>
              <Suspense fallback={<LoadingFallback />}>
                <SentryRoutes>
                  <Route path="/ttc/:orgId/:batchId" element={<TTCEmailEntry />} />
                  <Route path="/ttc/:orgId/:batchId/review" element={<TTCReviewPage />} />
                </SentryRoutes>
              </Suspense>
            </ChunkErrorBoundary>
          ) : isPublicPage ? (
            // Public pages - no auth required (e.g., trial extension from email)
            <ChunkErrorBoundary>
              <Suspense fallback={<LoadingFallback />}>
                <ChatwootWidget />
                <SentryRoutes>
                  <Route path="/extend-trial" element={<ExtendTrialPage />} />
                  <Route path="/unsubscribe" element={<UnsubscribePage />} />
                  <Route path="/auth/magic" element={<AuthMagicPage />} />
                  <Route path="/pay/:orgId/:invoiceId" element={<InvoicePayPage />} />
                </SentryRoutes>
              </Suspense>
            </ChunkErrorBoundary>
          ) : (
            // All other pages get LoadingGuard protection with MaintenanceGuard wrapping
            <LoadingGuard requireUser={true} requireOrg={false}>
              <MaintenanceGuard>
                <TourProvider>
                <ChatwootWidget />
                <MainContainer className={collapsed ? "collapsed" : ""}>
                  <Nav collapsed={collapsed} setCollapsed={setCollapsedByUser} />
                  {paletteEnabled() && (
                    <Suspense fallback={null}>
                      <CommandPalette />
                    </Suspense>
                  )}
                  {ledgerEnabled() && (
                    <Suspense fallback={null}>
                      <LedgerDrawer />
                    </Suspense>
                  )}
                  <TopNavAndContentContainer
                    className={collapsed ? "collapsed" : ""}
                    id="top-nav-and-content-container"
                    collapsed={collapsed}
                    style={{
                      boxSizing: "border-box",
                      paddingRight: dockedSqueeze ? LEDGER_DRAWER_W : 0,
                      transition: "padding-right .2s ease",
                    }}>
                    {!shouldHideTopNav && <TopNav collapsed={collapsed} />}
                    <ControlBanner />
                    {trialEnabled() && <TrialBanner />}
                    {trialEnabled() && <EmailPreviewModal />}
                    <ContentContainer
                      id="content-container"
                      className={collapsed ? "collapsed" : ""}
                      collapsed={collapsed}>
                      <ChunkErrorBoundary>
                        <Suspense fallback={<LoadingFallback />}>
                          <UnonboardedOrgGuard>
                          <SentryRoutes>
                          <Route path="/" element={<Home />} />

                          <Route path="/migration/postgres" element={<PostgresProofPage />} />
                          <Route path="/foundation" element={<Foundation />} />
                          {/* <Route path="/transactions" element={<Transactions />} /> */}
                          <Route
                            path="/contacts/contacts"
                            element={<ContactsPage />}
                          />
                          <Route
                            path="/contacts/donors"
                            element={<DonorsPage />}
                          />
                          <Route path="/history" element={<History />} />
                          <Route path="/account-settings" element={<Account />} />
                          {/* LLM-guided onboarding chat (W1, test orgs). TODO: gate behind a flag / route brand-new orgs here. */}
                          <Route path="/onboarding/chat" element={<OnboardingChat />} />
                          <Route path="/upkeep/ttc" element={<TTCDashboard />} />
                          <Route path="/upkeep/ttc/batch/:batchId" element={<TTCBatchDetail />} />
                          <Route path="/upkeep/ttc/decision-makers" element={<DecisionMakerManagement />} />
                          <Route path="/admin-t3" element={<AdminT3 />} />
                          <Route path="/super-admin" element={<AdminOrgs />} />
                          <Route path="/admin-management" element={<AdminManagement />} />
                          <Route path="/admin-bookkeeping" element={<AdminBookkeeping />} />
                          <Route path="/firm-management" element={<FirmManagementDashboard />} />
                          <Route path="/bookkeeper" element={<BookkeeperDashboard />} />
                          <Route
                            path="/admin-unit-tests"
                            element={<UnitTests />}
                          />
                          <Route
                            path="/admin-conversion"
                            element={<AdminMail />}
                          />
                          <Route
                            path="/admin-conversion/sequence/:sequenceId"
                            element={<SequenceEditorPage />}
                          />
                          <Route
                            path="/admin/:orgId/org-setup"
                            element={<OrgSetupReport />}
                          />
                          <Route
                            path="/maintenance"
                            element={<MaintenancePage />}
                          />
                          {/* All subroutes here */}
                          <Route
                            path="/upkeep/bank-accounts"
                            element={<BankAccounts />}
                          />
                          <Route
                            path="/upkeep/credit-cards"
                            element={<CreditCards />}
                          />
                          {/* <Route
                        path="/banking/integrations"
                        element={<Integrations />}
                      /> */}
                          <Route
                            path="/upkeep/reconciliation"
                            element={<Reconciliation />}
                          />
                          <Route
                            path="/banking/auto-reconciliation/:autoRecId"
                            element={<AutoReconciliationStatus />}
                          />
                          <Route
                            path="/banking/auto-reconciliation/:autoRecId/review"
                            element={<AutoReconciliationReview />}
                          />
                          <Route
                            path="/banking/:accountId/handle-imported"
                            element={<HandleImportedTransactions />}
                          />
                          <Route
                            path="/upkeep/donations"
                            element={<Donations />}
                          />
                          <Route
                            path="/upkeep/donations/sources/:sourceId"
                            element={<SourceManagementPage />}
                          />
                          {/* <Route
                        path="/transactions/accounts-payable"
                        element={<AccountsPayable />}
                      />
                      <Route
                        path="/transactions/accounts-receivable"
                        element={<AccountsReceivable />}
                      /> */}
                          <Route
                            path="/upkeep/journal-entry"
                            element={<JournalEntry />}
                          />
                          <Route
                            path="/upkeep/payables-receivables"
                            element={<PayablesReceivables />}
                          />
                          {/* <Route
                        path="/transactions/transfers"
                        element={<Transfers />}
                      /> */}
                          <Route
                            path="/reports"
                            element={<ReportsIndexRedirect />}
                          />
                          <Route
                            path="/reports/balance-sheet"
                            element={<BalanceSheet />}
                          />
                          <Route
                            path="/reports/income-statement"
                            element={<IncomeStatement />}
                          />
                          <Route
                            path="/reports/general-ledger"
                            element={<GeneralLedger />}
                          />
                          <Route
                            path="/reports/fund-summary"
                            element={<FundSummary />}
                          />
                          {/* <Route
                        path="/reports/budget-to-actual"
                        element={<BudgetToActual />}
                      /> */}
                          <Route
                            path="/reports/transactions-list"
                            element={<TransactionsList />}
                          />
                          <Route
                            path="/reports/reconciliation"
                            element={<ReconciliationReport />}
                          />
                          <Route
                            path="/reports/donations"
                            element={<DonationStatements />}
                          />
                          <Route
                            path="/reports/saved-packages"
                            element={<Navigate to="/reports/library" replace />}
                          />
                          <Route
                            path="/reports/library"
                            element={<ReportsLibraryPage />}
                          />
                          <Route
                            path="/reports/custom-reports"
                            element={<CustomReportsPage />}
                          />
                          <Route
                            path="/foundation/chart-of-accounts"
                            element={<ChartOfAccounts />}
                          />
                          <Route
                            path="/foundation/funds-list"
                            element={<FundsList />}
                          />
                          <Route
                            path="/foundation/labels-list"
                            element={<LabelsList />}
                          />
                          <Route
                            path="/foundation/budgets"
                            element={<Budgets />}
                          />
                          <Route
                            path="/foundation/consolidation"
                            element={<ConsolidationMapping />}
                          />
                          {/* Foundation Donations - Page-based architecture for OAuth flows */}
                          <Route
                            path="/foundation/donations"
                            element={<DonationSourcesListPage />}
                          />
                          <Route
                            path="/foundation/donations/sources/connect"
                            element={<SelectSourceType />}
                          />
                          <Route
                            path="/foundation/donations/sources/connect/pushpay"
                            element={<ConnectPushpay />}
                          />
                          <Route
                            path="/foundation/donations/sources/connect/pushpay/mapping"
                            element={<PushpayFundMapping />}
                          />
                          <Route
                            path="/foundation/donations/sources/connect/t3donations"
                            element={<ConnectT3Donations />}
                          />
                          <Route
                            path="/foundation/donations/sources/connect/manual"
                            element={<ConnectManualSource />}
                          />
                          <Route
                            path="/foundation/donations/sources/:sourceId"
                            element={<SourceManagementPage />}
                          />
                          <Route
                            path="/foundation/integrations"
                            element={<IntegrationsHub />}
                          />
                          <Route
                            path="/foundation/integrations/billcom/mapping"
                            element={<BillComMappingPage />}
                          />
                          <Route
                            path="/foundation/payroll"
                            element={<PayrollHub />}
                          />
                          <Route
                            path="/account/org-management"
                            element={<OrgInfo />}
                          />
                          <Route path="/account/users" element={<Users />} />
                          <Route
                            path="/account/user-management"
                            element={<UserManagement />}
                          />
                          <Route
                            path="/account/preferences"
                            element={<Preferences />}
                          />
                          <Route
                            path="/account/subscriptions"
                            element={<SubscriptionManagement />}
                          />
                          <Route
                            path="/account/billing"
                            element={<BillingPage />}
                          />
                          <Route
                            path="/account/welcome-to-the-club"
                            element={<WelcomeToTheClub />}
                          />
                          <Route path="/theme-demo" element={<ThemeDemo />} />
                          <Route path="*" element={<NoMatch />} />
                          {/* <Route path="/test" element={<Test />} /> */}
                          </SentryRoutes>
                          </UnonboardedOrgGuard>
                        </Suspense>
                      </ChunkErrorBoundary>
                    </ContentContainer>
                  </TopNavAndContentContainer>
                </MainContainer>
              </TourProvider>
              </MaintenanceGuard>
            </LoadingGuard>
          )}
        </ActivityTracker>
      </UserProvider>
    );
  };

  const AppWithRouter = () => (
    <BrowserRouter>
      <AppInSession />
    </BrowserRouter>
  );

  return (
    <ThemeProvider>
      <ProvideAuth>
        <AppWithRouter />
      </ProvideAuth>
    </ThemeProvider>
  );
};

export default App;
